/**
 * Classify an Auth0 SDK failure. Ordered, CLOSED partition — both special sets
 * are closed and everything else defaults to transient, because every responded
 * SDK failure carries a `.error` (spa-js's fallback is
 * `new GenericError(error || 'request_error', …)`), so an open
 * "recognized-but-unlisted → fatal" rule would terminalize recoverable infra
 * errors. Reads the code from `.error` (GenericError's field), never `.code`.
 */
const REAUTH_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'mfa_required',
  // forward-looking — unreachable until useRefreshTokens/useMrrt (see spec R3):
  'missing_refresh_token',
  'invalid_grant',
  'missing_scopes',
]);

// access_denied is the only LIVE fatal code. invalid_configuration is thrown at
// Auth0Client construction (surfaces to an error boundary), never reaching here.
const FATAL_CODES = new Set(['access_denied']);

// Where spa-js puts an Auth0 error code (GenericError's `.error` field, never
// `.code`) — the one place that knowledge lives, so callers read the code
// through this instead of re-deriving it.
export function getAuthErrorCode(error: unknown): string | undefined {
  if (!(error instanceof Error)) return undefined;
  const code = (error as { error?: unknown }).error;
  return typeof code === 'string' ? code : undefined;
}

export function classifyAuthError(error: unknown): 'reauth' | 'transient' | 'fatal' {
  if (!(error instanceof Error)) return 'fatal';
  const code = getAuthErrorCode(error);
  if (code !== undefined && REAUTH_CODES.has(code)) return 'reauth';
  if (code !== undefined && FATAL_CODES.has(code)) return 'fatal';
  return 'transient';
}
